import { useEffect } from 'react';
import { Row, Col, Grid } from 'antd';
import { Navigate, useLocation } from 'react-router';
import {
  Forgot, ModifyPassword, Login, EmailSent,
} from 'app/auth/components';
import { IconSvg } from 'shared/icon';
import { LocalStorageService, i18n } from 'services';
import { useTimeZone } from 'hooks';
import Notification from 'app/layout/components/Notification';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { selectUserConfigs } from 'app/userPreferences/selectors';
import { selectPageLoading } from 'app/settings/selectors';
import { useGetLatestReleaseNoteQuery } from 'features/releaseNote/api';
import { selectAuthenticatedStatus, selectAuthUser } from 'app/auth/selectors';
import { setAuthenticated } from 'app/auth/slices';
import { authApi } from 'app/auth/api';
import { User } from 'app/auth/types';
import {
  StyledLoginWrapper, StyledSeparator, StyledLoginAside, StyledAsideWrapper, StyledLogo, StyledAsideText, StyledCopyright, StyledLogoMobile,
} from 'app/auth/components/Login.styled';
import { StyledAuthSpin } from 'components/styledComponents/spin/Spin.styled';
import { resetAppState } from 'store/actions';

const t = i18n.getFixedT('en');

const { useResetPasswordMutation, useForgotPasswordMutation, useResendTFAMutation } = authApi;

const Auth = () => {
  const userConfigs = useAppSelector(selectUserConfigs);
  const pageLoading = useAppSelector(selectPageLoading);

  const isAuthenticated = useAppSelector(selectAuthenticatedStatus);
  const user = useAppSelector(selectAuthUser);

  const [resetPassword] = useResetPasswordMutation();
  const [forgotPassword] = useForgotPasswordMutation();
  const [resendTFA] = useResendTFAMutation();
  const dispatch = useAppDispatch();
  const { getDateTz } = useTimeZone();

  const { state, pathname } = useLocation();

  const { useBreakpoint } = Grid;

  const screens = useBreakpoint();

  const breakPointMd = screens.md;
  const breakPointXl = screens.xl;

  // clear redux store, when I go to Login page and localStorage is clear
  useEffect(() => {
    if (!LocalStorageService.get('auth')) {
      dispatch(resetAppState());
    }
  }, [dispatch]);

  const { from } = state || { from: '/' };

  const pages = {
    resetPassword: <ModifyPassword title="Reset" text={t('notification:resetPassword')} send={resetPassword} />,
    forgotPassword: <Forgot title="Recover Password" text={t('notification:forgotPassword')} send={forgotPassword} isPassword />,
    emailSent: <EmailSent />,
    login: <Login />,
    setPassword: <ModifyPassword title="Set" text={t('notification:setPassword')} send={resetPassword} />,
    resendTFA: <Forgot title="Recover 2FA" text={t('notification:forgotTFA')} send={resendTFA} />,
  } as const;

  useGetLatestReleaseNoteQuery(undefined, { skip: !(user && userConfigs && LocalStorageService.get('auth')) });

  const handlePermissionCheck = (data: User) => {
    const { permissions = {}, userClaims } = data;
    // Don't have permission page

    if (!userClaims?.length && !Object.keys(permissions).length) {
      return <Navigate to="/noAccess" replace />;
    }

    return <Navigate to={from} replace />;
  };

  // if both of them are in localStorage or in redux
  if (user && userConfigs && LocalStorageService.get('auth')) {
    if (!isAuthenticated) {
      setAuthenticated(true);
    }
    // re-write user to localStorage
    LocalStorageService.set('user', user);

    return handlePermissionCheck(user);
  }
  type PageKey = keyof typeof pages;
  const page = pathname.substring(1);
  const pageKey = Object.keys(pages).find((el) => el.toLowerCase() === page.toLowerCase());
  const Component = pageKey ? pages[pageKey as PageKey] : null;

  return (
    <>
      <Notification />
      <StyledAuthSpin spinning={pageLoading} />
      <StyledLoginWrapper md={breakPointMd ? 1 : 0}>
        <Row gutter={24} justify="center">
          <Col xxl={6} xl={8} lg={10} md={10} sm={22} xs={24}>
            <StyledLoginAside>
              <StyledAsideWrapper md={breakPointMd ? 1 : 0} xl={breakPointXl ? 1 : 0}>
                <StyledLogo md={breakPointMd ? 1 : 0}>
                  <IconSvg icon="LoginPageIcon" width="28.5rem" height="28.5rem" color="" />
                </StyledLogo>
                <StyledLogoMobile md={breakPointMd ? 1 : 0}>
                  <IconSvg icon="CentrivoIcon" width="20rem" height="3.28rem" color="" />
                </StyledLogoMobile>
                <StyledAsideText md={breakPointMd ? 1 : 0} xl={breakPointXl ? 1 : 0}>
                  <IconSvg icon="CentrivoIcon" width="16rem" height="4.14rem" color="" />
                  <p>The world’s most powerful and flexible gaming platform.</p>
                </StyledAsideText>
              </StyledAsideWrapper>
            </StyledLoginAside>
          </Col>
          <Col xxl={6} xl={4} lg={2} md={2}>
            <StyledSeparator md={breakPointMd ? 1 : 0} />
          </Col>
          <Col xxl={6} xl={8} lg={10} md={10} sm={22} xs={24}>
            {Component}
          </Col>
        </Row>
        <StyledCopyright md={breakPointMd ? 1 : 0}>{`Copyright ${getDateTz().year()} Digitain. All Rights Reserved`}</StyledCopyright>
      </StyledLoginWrapper>
    </>
  );
};

export default Auth;
